import React from 'react';

import { Stack, Tooltip } from '@mui/material';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Link as NavLink } from 'react-router';
import * as Yup from 'yup';

import { sendVerificationCodeViaEmail } from '../../../../api/security';
import {
  ButtonStyled,
  ButtonTextStyled,
  FormTextLabelStyled,
  TextFieldStyled,
  TooltipSx,
  TypographyInfoStyled,
} from '../../styles';
import { IEmailForm } from '../../types';

interface IProps {
  onComplete: (email: string) => void;
}

const EmailForm = React.memo(({ onComplete }: IProps) => {
  const [isProcess, setProcess] = React.useState<boolean>(false);
  const [emailNotFound, setEmailNotFound] = React.useState<boolean>(false);

  const { t } = useTranslation();

  const onSubmit = React.useCallback(
    async (values: IEmailForm) => {
      setProcess(true);

      try {
        const { status } = await sendVerificationCodeViaEmail(values);

        if (status) {
          onComplete(values.email);
        } else {
          setEmailNotFound(true);
        }
      } catch {
        setEmailNotFound(true);
      } finally {
        setProcess(false);
      }
    },
    [onComplete, setEmailNotFound],
  );

  return (
    <Formik
      initialValues={{
        email: '',
      }}
      validationSchema={Yup.object({
        email: Yup.string()
          .email(t('signin.email.form_error'))
          .required(t('signin.email.form_error')),
      })}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <Form>
          <Stack spacing={3} alignItems="center">
            <Stack direction="row" spacing={2} alignItems="flex-end">
              <Stack spacing={1}>
                <FormTextLabelStyled htmlFor="email">{t('general.email')}</FormTextLabelStyled>

                <Tooltip
                  title={emailNotFound ? t('signin.email.not_found') : formik.errors.email}
                  open={emailNotFound || !!formik.errors.email}
                  arrow
                  placement="right"
                  slotProps={{
                    tooltip: {
                      sx: TooltipSx,
                    },
                  }}
                >
                  <TextFieldStyled
                    variant="outlined"
                    autoComplete="off"
                    {...formik.getFieldProps('email')}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    id="email"
                  />
                </Tooltip>
              </Stack>

              <ButtonStyled
                variant="contained"
                color="primary"
                disabled={!formik.dirty || !formik.isValid || isProcess}
                onClick={() => formik.handleSubmit()}
              >
                {t('signin.email.send')}
              </ButtonStyled>
            </Stack>

            <Stack width="100%" alignItems="center">
              {!emailNotFound && (
                <TypographyInfoStyled>{t('signin.email.info')}</TypographyInfoStyled>
              )}

              {emailNotFound && (
                <ButtonTextStyled variant="text" component={NavLink} to="/auth/signup">
                  {t('signin.email.signup')}
                </ButtonTextStyled>
              )}
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
});

export default EmailForm;
